import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { chartOfAccountsService } from '../../services/accounting/chart-of-accounts';
import { journalEntriesService } from '../../services/accounting/journal-entries';
import { XMarkIcon, ArrowDownTrayIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

const GeneralLedgerModal = ({ onClose }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [accountTransactions, setAccountTransactions] = useState([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [searchTerm, setSearchTerm] = useState('');

  const loadAccounts = useCallback(async () => {
    if (!currentCompany?.id) return;
    
    setLoading(true);
    try {
      const accountsData = await chartOfAccountsService.getChartOfAccounts(currentCompany.id);
      const activeAccounts = accountsData.filter(account => account.isActive);
      setAccounts(activeAccounts);
      
      // Auto-select first account if available
      if (activeAccounts.length > 0 && !selectedAccountId) {
        setSelectedAccountId(activeAccounts[0].id);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error(t('bank_reconciliation.error_loading_accounts'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany?.id, selectedAccountId, t]);

  const loadAccountTransactions = useCallback(async () => {
    if (!selectedAccountId || !currentCompany?.id) return;
    
    setLoading(true);
    try {
      // Get all journal entries for the company
      const journalEntries = await journalEntriesService.getJournalEntries(currentCompany.id);
      
      // Filter entries within date range
      const filteredEntries = journalEntries.filter(entry => {
        const entryDate = new Date(entry.date);
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        return entryDate >= startDate && entryDate <= endDate;
      });

      // Extract line items for the selected account
      const transactions = [];
      filteredEntries.forEach(entry => {
        entry.lineItems?.forEach(lineItem => {
          if (lineItem.accountId === selectedAccountId) {
            transactions.push({
              ...lineItem,
              date: entry.date,
              entryId: entry.id,
              entryNumber: entry.entryNumber,
              entryStatus: entry.status,
              entryType: entry.entryType,
              createdAt: entry.createdAt
            });
          }
        });
      });

      // Sort by date (newest first)
      transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setAccountTransactions(transactions);
    } catch (error) {
      console.error('Error loading account transactions:', error);
      toast.error(t('reports.error_loading_transactions', 'Failed to load account transactions'));
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, currentCompany?.id, dateRange, t]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    if (selectedAccountId) {
      loadAccountTransactions();
    }
  }, [selectedAccountId, loadAccountTransactions]);

  const calculateRunningBalance = (transactions) => {
    const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
    if (!selectedAccount) return transactions;

    let runningBalance = selectedAccount.balance || 0;
    
    // Since transactions are sorted newest first, we need to work backwards
    const reversedTransactions = [...transactions].reverse();
    
    reversedTransactions.forEach(transaction => {
      const amount = (transaction.debit || 0) - (transaction.credit || 0);
      
      // Apply accounting rules
      if (selectedAccount.accountType === 'asset' || selectedAccount.accountType === 'expense') {
        // Assets and expenses: debits increase, credits decrease
        runningBalance -= amount;
      } else {
        // Liabilities, equity, revenue: credits increase, debits decrease
        runningBalance += amount;
      }
      
      transaction.balanceBeforeTransaction = runningBalance;
    });

    // Now calculate forward running balance
    runningBalance = selectedAccount.balance || 0;
    transactions.forEach(transaction => {
      transaction.balanceAfterTransaction = runningBalance;
      
      const amount = (transaction.debit || 0) - (transaction.credit || 0);
      
      if (selectedAccount.accountType === 'asset' || selectedAccount.accountType === 'expense') {
        runningBalance -= amount;
      } else {
        runningBalance += amount;
      }
    });

    return transactions;
  };

  const filteredTransactions = accountTransactions.filter(transaction =>
    transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transaction.entryNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const transactionsWithBalance = calculateRunningBalance(filteredTransactions);

  const selectedAccount = accounts.find(acc => acc.id === selectedAccountId);
  const totalDebits = accountTransactions.reduce((sum, txn) => sum + (txn.debit || 0), 0);
  const totalCredits = accountTransactions.reduce((sum, txn) => sum + (txn.credit || 0), 0);

  const exportToPDF = async () => {
            toast(t('reports.export_coming_soon', 'PDF export coming soon'));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { class: 'badge badge-warning badge-xs', text: t('quotations.draft') },
      posted: { class: 'badge badge-success badge-xs', text: t('accounting.posted') },
      reversed: { class: 'badge badge-error badge-xs', text: t('accounting.reversed', 'Reversed') }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return <span className={config.class}>{config.text}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('accounting.general_ledger')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedAccount ? `${selectedAccount.accountCode} - ${selectedAccount.accountName}` : t('accounting.select_account')}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={exportToPDF}
                className="btn btn-sm btn-outline"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                {t('accounting.export_pdf')}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('accounting.account')}
              </label>
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="select select-bordered w-full select-sm"
              >
                <option value="">{t('accounting.select_account')}</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.accountCode} - {account.accountName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.start_date')}
              </label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="input input-bordered w-full input-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.end_date')}
              </label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="input input-bordered w-full input-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('common.search')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('reports.search_transactions', 'Search transactions...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input input-bordered w-full input-sm pl-8"
                />
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Account Summary */}
        {selectedAccount && (
          <div className="px-6 py-3 bg-blue-50 border-b">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('accounting.account_type')}</div>
                <div className="font-semibold text-blue-900 capitalize">{selectedAccount.accountType}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('reports.current_balance', 'Current Balance')}</div>
                <div className="font-semibold text-blue-900">
                  SAR {Math.abs(selectedAccount.balance || 0).toFixed(2)}
                </div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('accounting.total_debits')}</div>
                <div className="font-semibold text-green-600">SAR {totalDebits.toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-600">{t('accounting.total_credits')}</div>
                <div className="font-semibold text-red-600">SAR {totalCredits.toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : !selectedAccountId ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('reports.select_account_to_view', 'Select an Account to View Ledger')}
              </h3>
              <p className="text-gray-500">
                {t('reports.general_ledger_desc', 'Choose an account from the dropdown to see its detailed transaction history')}
              </p>
            </div>
          ) : transactionsWithBalance.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">📝</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('reports.no_transactions', 'No Transactions Found')}
              </h3>
              <p className="text-gray-500">
                {t('reports.no_transactions_desc', 'This account has no transactions in the selected date range')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left font-semibold">{t('chart.date')}</th>
                    <th className="text-left font-semibold">{t('reports.entry_ref', 'Entry Ref')}</th>
                    <th className="text-left font-semibold">{t('accounting.description')}</th>
                    <th className="text-right font-semibold">{t('accounting.debit')}</th>
                    <th className="text-right font-semibold">{t('accounting.credit')}</th>
                    <th className="text-right font-semibold">{t('accounting.balance')}</th>
                    <th className="text-center font-semibold">{t('accounting.table.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactionsWithBalance.map((transaction, index) => (
                    <tr key={`${transaction.entryId}-${index}`}>
                      <td className="font-medium">
                        {new Date(transaction.date).toLocaleDateString()}
                      </td>
                      <td className="font-mono text-sm">
                        {transaction.entryNumber}
                      </td>
                      <td className="max-w-xs truncate" title={transaction.description}>
                        {transaction.description}
                      </td>
                      <td className="text-right">
                        {(transaction.debit || 0) > 0 ? (
                          <span className="font-semibold text-green-600">
                            SAR {(transaction.debit || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-right">
                        {(transaction.credit || 0) > 0 ? (
                          <span className="font-semibold text-red-600">
                            SAR {(transaction.credit || 0).toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="text-right font-semibold">
                        SAR {(transaction.balanceAfterTransaction || 0).toFixed(2)}
                      </td>
                      <td className="text-center">
                        {getStatusBadge(transaction.entryStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-bold border-t-2 border-gray-300">
                    <td colSpan="3" className="text-right font-bold">
                      {t('journal.totals')}
                    </td>
                    <td className="text-right font-bold text-green-600">
                      SAR {totalDebits.toFixed(2)}
                    </td>
                    <td className="text-right font-bold text-red-600">
                      SAR {totalCredits.toFixed(2)}
                    </td>
                    <td className="text-right font-bold">
                      SAR {Math.abs(selectedAccount?.balance || 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {t('reports.showing_transactions', 'Showing')} {transactionsWithBalance.length} {t('reports.transactions', 'transactions')}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
              >
                {t('common.close', t('common.close'))}
              </button>
              <button
                type="button"
                onClick={loadAccountTransactions}
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  '🔄'
                )}
                {t('common.refresh')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralLedgerModal; 